import React, { useEffect, useState, useRef, Component } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { CalendarIcon, UserIcon, TagIcon, ArrowLeftIcon, ArrowRightIcon, BookOpenIcon, ChevronLeftIcon, ChevronRightIcon, ShareIcon, ClockIcon, LinkedinIcon, TwitterIcon, FacebookIcon, CopyIcon, CheckIcon, MailIcon, LightbulbIcon, BookmarkIcon, HeartIcon, ThumbsUpIcon, MessageCircleIcon, StarIcon, ListIcon, AlertCircleIcon } from 'lucide-react';
import { BlogPost as BlogPostType, sampleBlogPosts } from '../data/sampleBlogPosts';
export function BlogPost() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);
  const [showTableOfContents, setShowTableOfContents] = useState(false);
  const [currentPost, setCurrentPost] = useState<BlogPostType | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previousPost, setPreviousPost] = useState<BlogPostType | null>(null);
  const [nextPost, setNextPost] = useState<BlogPostType | null>(null);
  const articleRef = useRef<HTMLElement>(null);
  // Fetch the current post and related posts
  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setError('');
      try {
        // Get all posts for navigation
        const allPosts = sampleBlogPosts;
        // Check if this is one of our sample posts first
        const samplePost = allPosts.find(post => post.id === id);
        if (samplePost) {
          setCurrentPost(samplePost);
          // Find related posts (other sample posts in the same category)
          const related = allPosts.filter(post => post.id !== id && post.category === samplePost.category).slice(0, 3);
          setRelatedPosts(related);
          // Find previous and next posts for navigation
          const currentIndex = allPosts.findIndex(post => post.id === id);
          if (currentIndex > 0) {
            setPreviousPost(allPosts[currentIndex - 1]);
          } else {
            setPreviousPost(null);
          }
          if (currentIndex < allPosts.length - 1) {
            setNextPost(allPosts[currentIndex + 1]);
          } else {
            setNextPost(null);
          }
          setLoading(false);
          return;
        }
        // If not a sample post, try to fetch from API
        const response = await fetch(`http://localhost:3001/posts/${id}`, {
          signal: AbortSignal.timeout(5000) // Add timeout
        });
        if (!response.ok) {
          throw new Error('Post not found');
        }
        const post = await response.json();
        setCurrentPost(post);
        // Fetch all posts for navigation
        const allPostsResponse = await fetch('http://localhost:3001/posts');
        if (!allPostsResponse.ok) {
          throw new Error('Failed to fetch posts for navigation');
        }
        const allPostsData = await allPostsResponse.json();
        const apiPosts = [...allPostsData, ...allPosts.filter(p => !allPostsData.some((ap: BlogPostType) => ap.id === p.id))];
        // Find related posts (same category, excluding current post)
        const related = apiPosts.filter(p => p.id !== post.id && p.category === post.category).slice(0, 3);
        setRelatedPosts(related);
        // Find previous and next posts
        const currentIndex = apiPosts.findIndex(p => p.id === post.id);
        if (currentIndex > 0) {
          setPreviousPost(apiPosts[currentIndex - 1]);
        }
        if (currentIndex < apiPosts.length - 1) {
          setNextPost(apiPosts[currentIndex + 1]);
        }
      } catch (err: any) {
        console.error('Error fetching post:', err);
        // If the post ID exists in our sample posts but the API call failed
        const samplePost = sampleBlogPosts.find(post => post.id === id);
        if (samplePost) {
          setCurrentPost(samplePost);
          // Get all sample posts
          const allPosts = sampleBlogPosts;
          // Find related posts
          const related = allPosts.filter(post => post.id !== id && post.category === samplePost.category).slice(0, 3);
          setRelatedPosts(related);
          // Set up navigation
          const currentIndex = allPosts.findIndex(post => post.id === id);
          if (currentIndex > 0) {
            setPreviousPost(allPosts[currentIndex - 1]);
          }
          if (currentIndex < allPosts.length - 1) {
            setNextPost(allPosts[currentIndex + 1]);
          }
        } else {
          setError(err.message || 'An error occurred');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [id]);
  // Handle reading progress
  useEffect(() => {
    const handleScroll = () => {
      if (!articleRef.current) return;
      const element = articleRef.current;
      const totalHeight = element.clientHeight;
      const windowHeight = window.innerHeight;
      const scrollTop = window.scrollY - element.offsetTop;
      if (scrollTop < 0) {
        setReadingProgress(0);
        return;
      }
      const scrolled = Math.min(scrollTop / (totalHeight - windowHeight), 1);
      setReadingProgress(Math.min(scrolled, 1));
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Reset copied state after 2 seconds
  useEffect(() => {
    if (copied) {
      const timer = setTimeout(() => {
        setCopied(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copied]);
  // Function to extract headings from content for table of contents
  const extractHeadings = (content: string) => {
    const headings: {
      text: string;
      id: string;
    }[] = [];
    if (!content) return headings;
    const regex = /<h2[^>]*>(.*?)<\/h2>/g;
    let match;
    while ((match = regex.exec(content)) !== null) {
      // Extract text without HTML tags
      const text = match[1].replace(/<[^>]*>/g, '');
      const id = text.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
      headings.push({
        text,
        id
      });
    }
    return headings;
  };
  // Extract headings for table of contents
  const headings = currentPost ? extractHeadings(currentPost.content || '') : [];
  // Calculate reading time in minutes
  const calculateReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const textOnly = content.replace(/<[^>]*>/g, '');
    const wordCount = textOnly.split(/\s+/).length;
    return Math.ceil(wordCount / wordsPerMinute);
  };
  // Copy URL to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
  };
  // If loading, show a loading spinner
  if (loading) {
    return <div className="w-full bg-white py-20 flex justify-center" data-id="element-1065">
        <div className="w-16 h-16 border-t-4 border-gray-900 border-solid rounded-full animate-spin" data-id="element-1066"></div>
      </div>;
  }
  // If post not found, show a message and link back to blog
  if (!currentPost && !loading) {
    return <div className="py-20 w-full bg-white" data-id="element-1067">
        <div className="container mx-auto px-4 text-center" data-id="element-1068">
          <h1 className="text-2xl font-serif mb-4" data-id="element-1069">Blog Post Not Found</h1>
          <p className="mb-8 font-serif" data-id="element-1070">
            The blog post you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/blog" className="inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white rounded-md font-serif" data-id="element-1071">
            <ArrowLeftIcon size={18} className="mr-2" data-id="element-1072" />
            Back to Blog
          </Link>
        </div>
      </div>;
  }
  // If there's an error, show error message
  if (error) {
    return <div className="py-20 w-full bg-white" data-id="element-1073">
        <div className="container mx-auto px-4 text-center" data-id="element-1074">
          <div className="p-6 bg-red-50 rounded-lg text-red-700 mb-8 inline-flex items-center font-serif" data-id="element-1075">
            <AlertCircleIcon size={24} className="mr-2" data-id="element-1076" />
            <span data-id="element-1077">{error}</span>
          </div>
          <p className="mb-8 font-serif" data-id="element-1078">
            Please try again later or go back to the blog.
          </p>
          <Link to="/blog" className="inline-flex items-center justify-center px-6 py-3 bg-gray-900 text-white rounded-md font-serif" data-id="element-1079">
            <ArrowLeftIcon size={18} className="mr-2" data-id="element-1080" />
            Back to Blog
          </Link>
        </div>
      </div>;
  }
  return <div className="w-full bg-white font-serif" data-id="element-1081">
      {/* Reading Progress Bar */}
      <div className="fixed top-16 left-0 h-1 bg-gray-900 z-50 transition-all duration-300" style={{
      width: `${readingProgress * 100}%`,
      opacity: readingProgress > 0 ? 1 : 0
    }} data-id="element-1082"></div>
      {/* Header Navigation */}
      <div className="border-b border-gray-200" data-id="element-1083">
        <div className="container mx-auto px-4 py-4" data-id="element-1084">
          <Link to="/blog" className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm" data-id="element-1085">
            <ChevronLeftIcon size={16} className="mr-1" data-id="element-1086" />
            All Articles
          </Link>
        </div>
      </div>
      {/* Category and Date */}
      <div className="container mx-auto px-4 md:px-8 lg:px-16 pt-6 pb-2" data-id="element-1087">
        <div className="max-w-3xl mx-auto" data-id="element-1088">
          <div className="flex items-center justify-between text-xs uppercase tracking-widest text-gray-500 mb-2" data-id="element-1089">
            <span data-id="element-1090">{currentPost?.category}</span>
            <span data-id="element-1091">{currentPost?.date}</span>
          </div>
        </div>
      </div>
      {/* Article Title */}
      <header className="container mx-auto px-4 md:px-8 lg:px-16 pb-8 border-b border-gray-200" data-id="element-1092">
        <div className="max-w-3xl mx-auto" data-id="element-1093">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold mb-6 leading-tight" data-id="element-1094">
            {currentPost?.title}
          </h1>
          <p className="text-xl text-gray-700 mb-6 leading-relaxed" data-id="element-1095">
            {currentPost?.excerpt}
          </p>
          <div className="flex items-center" data-id="element-1096">
            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-serif text-xl mr-3" data-id="element-1097">
              {currentPost?.author.charAt(0)}
            </div>
            <div data-id="element-1098">
              <p className="font-medium" data-id="element-1099">By {currentPost?.author}</p>
              <p className="text-sm text-gray-500" data-id="element-1100">
                <span className="inline-block mr-3" data-id="element-1101">{currentPost?.date}</span>
                <span className="inline-block" data-id="element-1102">
                  <ClockIcon size={14} className="inline-block mr-1.5" data-id="element-1103" />
                  {currentPost?.readTime}
                </span>
              </p>
            </div>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 lg:px-16 py-12" data-id="element-1104">
        <div className="flex flex-col lg:flex-row max-w-6xl mx-auto" data-id="element-1105">
          {/* Sidebar for larger screens */}
          <div className="hidden lg:block w-48 flex-shrink-0 pr-12" data-id="element-1106">
            <div className="sticky top-24" data-id="element-1107">
              {/* Share Icons */}
              <div className="mb-8" data-id="element-1108">
                <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-4 border-b pb-2" data-id="element-1109">
                  Share
                </h3>
                <div className="flex flex-col space-y-4" data-id="element-1110">
                  <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(currentPost?.title || '')}`} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-[#1DA1F2] transition-colors" aria-label="Share on Twitter" data-id="element-1111">
                    <TwitterIcon size={18} data-id="element-1112" />
                  </a>
                  <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-[#4267B2] transition-colors" aria-label="Share on Facebook" data-id="element-1113">
                    <FacebookIcon size={18} data-id="element-1114" />
                  </a>
                  <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(currentPost?.title || '')}`} target="_blank" rel="noopener noreferrer" className="text-gray-700 hover:text-[#0077B5] transition-colors" aria-label="Share on LinkedIn" data-id="element-1115">
                    <LinkedinIcon size={18} data-id="element-1116" />
                  </a>
                  <a href={`mailto:?subject=${encodeURIComponent(currentPost?.title || '')}&body=${encodeURIComponent(window.location.href)}`} className="text-gray-700 hover:text-gray-900 transition-colors" aria-label="Share by Email" data-id="element-1117">
                    <MailIcon size={18} data-id="element-1118" />
                  </a>
                  <button onClick={copyToClipboard} className="text-gray-700 hover:text-gray-900 transition-colors" aria-label="Copy link" data-id="element-1119">
                    {copied ? <CheckIcon size={18} className="text-green-600" data-id="element-1120" /> : <CopyIcon size={18} data-id="element-1121" />}
                  </button>
                </div>
              </div>
              {/* Table of Contents */}
              {headings.length > 0 && <div className="mb-10" data-id="element-1122">
                  <h3 className="text-xs uppercase tracking-widest text-gray-500 mb-4 border-b pb-2" data-id="element-1123">
                    In This Article
                  </h3>
                  <nav className="space-y-2" data-id="element-1124">
                    {headings.map((heading, index) => <a key={index} href={`#${heading.id}`} className="block text-gray-600 hover:text-gray-900 transition-colors py-1 text-sm border-l-2 border-transparent hover:border-gray-900 pl-3" data-id="element-1125">
                        {heading.text}
                      </a>)}
                  </nav>
                </div>}
            </div>
          </div>
          {/* Main Article Content */}
          <article className="lg:flex-1 max-w-3xl" data-id="element-1126">
            {/* Key Takeaways Section */}
            {currentPost?.keyTakeaways && currentPost.keyTakeaways.length > 0 && <div className="mb-10 p-6 bg-gray-50 border-l-4 border-gray-900" data-id="element-1127">
                  <h2 className="text-lg font-serif font-bold mb-3" data-id="element-1128">
                    Key Points
                  </h2>
                  <ul className="space-y-3" data-id="element-1129">
                    {currentPost.keyTakeaways.map((takeaway, index) => <li key={index} className="flex items-start" data-id="element-1130">
                        <span className="text-gray-700 leading-relaxed" data-id="element-1131">
                          {takeaway}
                        </span>
                      </li>)}
                  </ul>
                </div>}
            {/* Article Content */}
            <div ref={articleRef} className="article-content" data-id="element-1132">
              {/* Process the content to add IDs to headings and apply NYT-style formatting */}
              <div dangerouslySetInnerHTML={{
              __html: currentPost?.content
              // Add drop cap to first paragraph
              ?.replace(/<p>([^<]+)/, (match, content) => {
                return `<p class="first-letter:float-left first-letter:text-7xl first-letter:pr-4 first-letter:font-bold">${content}`;
              })
              // Format headings
              ?.replace(/<h2[^>]*>(.*?)<\/h2>/g, (match, content) => {
                const id = content.replace(/<[^>]*>/g, '').toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, '-');
                return `<h2 id="${id}" class="text-2xl font-serif font-bold mt-12 mb-6 scroll-mt-24">${content}</h2>`;
              })?.replace(/<h3[^>]*>(.*?)<\/h3>/g, (match, content) => {
                return `<h3 class="text-xl font-serif font-semibold mt-8 mb-4">${content}</h3>`;
              })
              // Format paragraphs
              ?.replace(/<p>/g, '<p class="mb-6 text-lg leading-relaxed text-gray-800">')
              // Format lists
              ?.replace(/<ul>/g, '<ul class="mb-8 ml-6 space-y-3">')?.replace(/<li>/g, '<li class="text-lg relative pl-2 leading-relaxed before:content-[\'\'] before:absolute before:left-[-1rem] before:top-[0.7rem] before:h-1.5 before:w-1.5 before:rounded-full before:bg-gray-400">')
              // Format blockquotes
              ?.replace(/<blockquote>/g, '<blockquote class="my-8 pl-6 py-1 border-l-2 border-gray-900 italic text-2xl font-light text-gray-700">') || ''
            }} data-id="element-1133" />
            </div>
            {/* Tags */}
            <div className="mt-12 pt-6 border-t border-gray-200" data-id="element-1134">
              <div className="flex items-center" data-id="element-1135">
                <TagIcon size={16} className="text-gray-500 mr-3" data-id="element-1136" />
                <div className="flex flex-wrap gap-2" data-id="element-1137">
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm" data-id="element-1138">
                    {currentPost?.category}
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm" data-id="element-1139">
                    Content Marketing
                  </span>
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-sm" data-id="element-1140">
                    East Africa
                  </span>
                </div>
              </div>
            </div>
            {/* Author Bio */}
            <div className="mt-12 pt-8 border-t border-gray-200" data-id="element-1141">
              <div className="flex items-start" data-id="element-1142">
                <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 font-serif text-2xl mr-6" data-id="element-1143">
                  {currentPost?.author.charAt(0)}
                </div>
                <div data-id="element-1144">
                  <h3 className="font-bold text-lg mb-2" data-id="element-1145">
                    About {currentPost?.author}
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1146">
                    Content Strategist and Brand Storytelling Expert with over a
                    decade of experience helping East African businesses connect
                    with their audiences through authentic narratives.
                  </p>
                  <div className="flex space-x-3" data-id="element-1147">
                    <a href="https://twitter.com/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-900" data-id="element-1148">
                      <TwitterIcon size={16} data-id="element-1149" />
                    </a>
                    <a href="https://linkedin.com/" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-900" data-id="element-1150">
                      <LinkedinIcon size={16} data-id="element-1151" />
                    </a>
                    <a href="mailto:example@example.com" className="text-gray-500 hover:text-gray-900" data-id="element-1152">
                      <MailIcon size={16} data-id="element-1153" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
            {/* Mobile Share Options */}
            <div className="lg:hidden mt-10 pt-6 border-t border-gray-200" data-id="element-1154">
              <h3 className="text-sm font-medium mb-4 uppercase tracking-wider text-gray-500" data-id="element-1155">
                Share This Article
              </h3>
              <div className="flex space-x-4" data-id="element-1156">
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(currentPost?.title || '')}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-200 text-gray-700 hover:border-[#1DA1F2] hover:text-[#1DA1F2] transition-colors" aria-label="Share on Twitter" data-id="element-1157">
                  <TwitterIcon size={18} data-id="element-1158" />
                </a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-200 text-gray-700 hover:border-[#4267B2] hover:text-[#4267B2] transition-colors" aria-label="Share on Facebook" data-id="element-1159">
                  <FacebookIcon size={18} data-id="element-1160" />
                </a>
                <a href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(currentPost?.title || '')}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full border border-gray-200 text-gray-700 hover:border-[#0077B5] hover:text-[#0077B5] transition-colors" aria-label="Share on LinkedIn" data-id="element-1161">
                  <LinkedinIcon size={18} data-id="element-1162" />
                </a>
                <a href={`mailto:?subject=${encodeURIComponent(currentPost?.title || '')}&body=${encodeURIComponent(window.location.href)}`} className="p-2 rounded-full border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors" aria-label="Share by Email" data-id="element-1163">
                  <MailIcon size={18} data-id="element-1164" />
                </a>
                <button onClick={copyToClipboard} className="p-2 rounded-full border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900 transition-colors" aria-label="Copy link" data-id="element-1165">
                  {copied ? <CheckIcon size={18} className="text-green-600" data-id="element-1166" /> : <CopyIcon size={18} data-id="element-1167" />}
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>
      {/* Post Navigation */}
      <div className="border-t border-gray-200" data-id="element-1168">
        <div className="container mx-auto px-4 md:px-8 lg:px-16 py-8" data-id="element-1169">
          <div className="max-w-4xl mx-auto" data-id="element-1170">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1171">
              {previousPost ? <Link to={`/blog/${previousPost.id}`} className="group flex flex-col" data-id="element-1172">
                  <div className="flex items-center text-gray-500 mb-2" data-id="element-1173">
                    <ChevronLeftIcon size={16} className="mr-1" data-id="element-1174" />
                    <span className="text-xs uppercase tracking-wider" data-id="element-1175">
                      Previous Article
                    </span>
                  </div>
                  <h4 className="font-medium group-hover:text-gray-600 transition-colors line-clamp-2" data-id="element-1176">
                    {previousPost.title}
                  </h4>
                </Link> : <div data-id="element-1177"></div>}
              {nextPost ? <Link to={`/blog/${nextPost.id}`} className="group flex flex-col text-right" data-id="element-1178">
                  <div className="flex items-center justify-end text-gray-500 mb-2" data-id="element-1179">
                    <span className="text-xs uppercase tracking-wider" data-id="element-1180">
                      Next Article
                    </span>
                    <ChevronRightIcon size={16} className="ml-1" data-id="element-1181" />
                  </div>
                  <h4 className="font-medium group-hover:text-gray-600 transition-colors line-clamp-2" data-id="element-1182">
                    {nextPost.title}
                  </h4>
                </Link> : <div data-id="element-1183"></div>}
            </div>
          </div>
        </div>
      </div>
      {/* Related Articles */}
      {relatedPosts.length > 0 && <section className="bg-gray-50 py-16 w-full" data-id="element-1184">
          <div className="container mx-auto px-4 md:px-8 lg:px-16" data-id="element-1185">
            <div className="max-w-5xl mx-auto" data-id="element-1186">
              <h2 className="text-2xl font-serif font-bold mb-10 border-b border-gray-200 pb-4" data-id="element-1187">
                More in {currentPost?.category}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1188">
                {relatedPosts.map(post => <div key={post.id} className="group" data-id="element-1189">
                    <div className="mb-3" data-id="element-1190">
                      <span className="text-xs uppercase tracking-wider text-gray-500" data-id="element-1191">
                        {post.date}
                      </span>
                    </div>
                    <h3 className="text-xl font-serif font-medium mb-2 group-hover:text-gray-600 transition-colors" data-id="element-1192">
                      <Link to={`/blog/${post.id}`} data-id="element-1193">{post.title}</Link>
                    </h3>
                    <p className="text-gray-600 mb-3 line-clamp-2" data-id="element-1194">
                      {post.excerpt}
                    </p>
                    <Link to={`/blog/${post.id}`} className="text-gray-900 font-medium inline-flex items-center border-b border-gray-400 pb-1 hover:border-gray-900 transition-colors" data-id="element-1195">
                      <span data-id="element-1196">Read more</span>
                    </Link>
                  </div>)}
              </div>
              <div className="text-center mt-12" data-id="element-1197">
                <Link to="/blog" className="inline-flex items-center justify-center px-6 py-3 border border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white transition-colors font-medium" data-id="element-1198">
                  <span data-id="element-1199">View All Articles</span>
                  <ArrowRightIcon size={16} className="ml-2" data-id="element-1200" />
                </Link>
              </div>
            </div>
          </div>
        </section>}
      {/* Newsletter Section */}
      <section className="py-16 bg-white w-full border-t border-gray-200" data-id="element-1201">
        <div className="container mx-auto px-4 md:px-8 lg:px-16" data-id="element-1202">
          <div className="max-w-3xl mx-auto" data-id="element-1203">
            <div className="text-center mb-8" data-id="element-1204">
              <h2 className="text-2xl font-serif font-bold mb-4" data-id="element-1205">
                Subscribe to Our Newsletter
              </h2>
              <p className="text-gray-600 mb-6 max-w-xl mx-auto" data-id="element-1206">
                Get the latest insights on content strategy, brand storytelling,
                and digital marketing trends in East Africa delivered to your
                inbox.
              </p>
            </div>
            <form className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" data-id="element-1207">
              <input type="email" placeholder="Your email address" className="flex-grow px-4 py-3 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-gray-900 font-sans" required data-id="element-1208" />
              <button type="submit" className="bg-gray-900 text-white hover:bg-gray-800 px-6 py-3 transition-colors whitespace-nowrap font-sans" data-id="element-1209">
                Subscribe
              </button>
            </form>
            <p className="text-xs text-gray-500 mt-3 text-center" data-id="element-1210">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>
      {/* Divider */}
      <div className="container mx-auto px-4" data-id="element-1211">
        <div className="max-w-5xl mx-auto border-t border-gray-200" data-id="element-1212"></div>
      </div>
    </div>;
}